// Signal de módulo (per-tab, en memoria) para el prompt de feedback al revertir (D1).
//
// POR QUÉ existe: el prompt se dispara desde el chip de status (treatment), pero al revertir
// la variante pasa a control y el subtree treatment (MenuV2) se DESMONTA — si el prompt viviera
// dentro de ese chip, moriría con él. Este signal lo desacopla del ciclo de vida del chip: el
// chip solo "pide" mostrar el prompt; quien lo RENDERIZA es MenuVersionInvitation, que vive en
// el shell del menú y persiste a través del flip treatment→control.
//
// Mismo patrón que preview.ts: singleton de módulo + subscribe para useSyncExternalStore. NO se
// persiste (es un prompt de sesión, no una preferencia). Keyed por feature_key para soportar 2+
// features sin colisión.

type Listener = () => void;

// true = hay un prompt de feedback pendiente de mostrar para esta feature.
const requested = new Map<string, boolean>();
const listeners = new Map<string, Set<Listener>>();

function getListeners(featureKey: string): Set<Listener> {
  let set = listeners.get(featureKey);
  if (!set) {
    set = new Set();
    listeners.set(featureKey, set);
  }
  return set;
}

function notify(featureKey: string): void {
  getListeners(featureKey).forEach((l) => l());
}

export function isRevertFeedbackRequested(featureKey: string): boolean {
  return requested.get(featureKey) === true;
}

export function subscribeRevertFeedback(featureKey: string, listener: Listener): () => void {
  const set = getListeners(featureKey);
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

// El chip de status lo llama tras ejecutar revert('revert_commit'): pide que el shell muestre
// el prompt (omitible, fire-and-forget). No bloquea ni revierte el cambio ya hecho.
export function requestRevertFeedback(featureKey: string): void {
  if (requested.get(featureKey) === true) return;
  requested.set(featureKey, true);
  notify(featureKey);
}

// El shell lo llama al enviar el motivo o al omitir/cerrar el prompt.
export function clearRevertFeedback(featureKey: string): void {
  if (requested.get(featureKey) !== true) return;
  requested.set(featureKey, false);
  notify(featureKey);
}

// Reset de PRODUCCIÓN: al cambiar de sesión/sucursal un prompt pendiente del usuario anterior
// no debe sobrevivir. Coherente con resetFeatureVersionStore/Hydration/Draining/Preview.
export function resetRevertFeedbackState(): void {
  const keys = Array.from(requested.keys());
  requested.clear();
  keys.forEach((k) => notify(k));
}

// Solo para tests: limpia el estado de módulo entre casos.
export function __resetRevertFeedbackForTests(): void {
  requested.clear();
  listeners.clear();
}
